import { useEffect, useMemo, useRef, useState } from 'react';

import { IconCheck, IconChevronDown, IconX } from '../incidents/icons';

interface MultiSelectProps {
  label: string;
  placeholder: string;
  options: Array<{ value: string; label: string; sublabel?: string; mono?: boolean }>;
  value: string[];
  onChange: (next: string[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

/** Au-delà, une liste ne se parcourt plus à l'œil : on ouvre un champ de recherche. */
const SEARCH_THRESHOLD = 8;

/**
 * Plafond de lignes rendues à la fois. Certains comptes déclarent ~900 BUPO
 * (TRP IDF) : tout peindre fige le popover pour rien, puisque personne ne
 * parcourt 900 lignes — on affiche les premières et on invite à affiner.
 */
const MAX_RENDERED = 100;

/** Sélecteur multiple en popover, style filtre de header. */
export const MultiSelect = ({
  label,
  placeholder,
  options,
  value,
  onChange,
  isLoading,
  disabled,
}: MultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  // Repartir d'une recherche vierge à chaque ouverture : un filtre laissé de la
  // fois précédente donnerait une liste mystérieusement amputée. Le focus va au
  // champ de recherche — on passe par une ref plutôt que `autoFocus`, que la
  // règle d'accessibilité interdit à juste titre sur un chargement de page,
  // mais qui est ici la suite naturelle d'un clic délibéré de l'utilisateur.
  useEffect(() => {
    if (!open) setSearch('');
    else searchRef.current?.focus();
  }, [open]);

  const toggle = (val: string) => {
    onChange(value.includes(val) ? value.filter(v => v !== val) : [...value, val]);
  };

  const showSearch = options.length > SEARCH_THRESHOLD;

  const matching = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      opt =>
        opt.label.toLowerCase().includes(needle) ||
        opt.value.toLowerCase().includes(needle) ||
        (opt.sublabel?.toLowerCase().includes(needle) ?? false),
    );
  }, [options, search]);

  const rendered = matching.slice(0, MAX_RENDERED);
  const hidden = matching.length - rendered.length;

  const trigger = (() => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const sel = options.find(o => o.value === value[0]);
      return sel?.label ?? placeholder;
    }
    return `${label} : ${value.length}`;
  })();

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        disabled={disabled}
        className={[
          'inline-flex items-center gap-2 h-8 px-3 text-sm rounded-md border transition-colors duration-100',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          value.length > 0
            ? 'bg-zinc-900 text-white border-zinc-900 hover:bg-zinc-800'
            : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50',
        ].join(' ')}
      >
        <span className={value.length > 0 ? 'text-white' : 'text-zinc-500'}>{label}</span>
        <span className="text-xs">{value.length > 0 ? trigger : placeholder}</span>
        <IconChevronDown width={12} height={12} className={value.length > 0 ? 'text-white/70' : 'text-zinc-400'} />
      </button>

      {open ? (
        <div className="absolute top-full mt-1.5 left-0 z-50 w-72 bg-white border border-zinc-200 rounded-md shadow-md py-1.5 max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="px-3 py-6 text-sm text-zinc-500 text-center">Chargement…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-6 text-sm text-zinc-500 text-center">Aucune option disponible.</div>
          ) : (
            <>
              {showSearch ? (
                <div className="px-1.5 pb-1.5 border-b border-zinc-100">
                  <input
                    type="text"
                    ref={searchRef}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={`Rechercher parmi ${options.length}…`}
                    aria-label={`Rechercher dans ${label}`}
                    className="w-full h-8 px-2 text-sm bg-white border border-zinc-200 rounded text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 transition-colors"
                  />
                </div>
              ) : null}
              {value.length > 0 ? (
                <div className="px-1.5 pb-1.5 border-b border-zinc-100">
                  <button
                    type="button"
                    onClick={() => onChange([])}
                    className="w-full h-8 px-2 flex items-center gap-2 rounded text-xs text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors text-left"
                  >
                    <IconX width={12} height={12} />
                    <span>Tout effacer ({value.length})</span>
                  </button>
                </div>
              ) : null}
              {matching.length === 0 ? (
                <div className="px-3 py-6 text-sm text-zinc-500 text-center">Aucun résultat pour « {search} ».</div>
              ) : null}
              <ul className="flex flex-col px-1.5 pt-1">
                {rendered.map(opt => {
                  const checked = value.includes(opt.value);
                  return (
                    <li key={opt.value}>
                      <button
                        type="button"
                        onClick={() => toggle(opt.value)}
                        className="w-full min-h-[34px] px-2 py-1.5 flex items-center gap-2 rounded text-sm text-zinc-700 hover:bg-zinc-50 transition-colors text-left"
                      >
                        <span
                          className={[
                            'h-3.5 w-3.5 rounded-sm border inline-flex items-center justify-center shrink-0',
                            checked ? 'bg-zinc-900 border-zinc-900 text-white' : 'border-zinc-300 bg-white',
                          ].join(' ')}
                        >
                          {checked ? <IconCheck width={10} height={10} strokeWidth={3} /> : null}
                        </span>
                        <span className="flex-1 flex flex-col min-w-0">
                          <span className={['truncate', opt.mono ? 'font-mono text-xs tabular-nums' : ''].join(' ')}>
                            {opt.label}
                          </span>
                          {opt.sublabel ? <span className="text-xs text-zinc-500">{opt.sublabel}</span> : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {hidden > 0 ? (
                <p className="px-3 py-2 text-xs text-zinc-500 border-t border-zinc-100 mt-1">
                  {hidden} autre{hidden > 1 ? 's' : ''} — affinez la recherche.
                </p>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};
